// src/pages/admin/ThemeConfig.js
// 后台 · 管理中心 · 个性配置（doc01 3.6，缺陷 119）
// 自上而下：登录页 logo（png 128×400）→ 导航栏 logo（png 128×128）→ 网页 icon（ico 64×64）→ 系统名称（4-10 中文字符）→ 保存。
// 实现本地上传（本地预览，不接后端）+ 保存 + 实时更新：保存后写入 localStorage 并广播事件，
// 使导航栏 / 登录页 logo、网页 icon 与系统名称随之变化；未操作项保留原有 logo 与名称。
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Upload, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import PageSkeleton from '../../components/PageSkeleton';
import { findMenu, pick } from '../../config/menuConfig';

const labelStyle = { color: 'rgba(255,255,255,0.55)', fontSize: 12 };
const MAX = 2 * 1024 * 1024;

const ITEMS = [
  { key: 'loginLogo', label: '登录页 logo', spec: '格式 png · 尺寸 128px × 400px', hint: '支持 jpg/png/jpeg，不超过 2MB', usage: '应用于登录页和系统正常模式下的导航栏' },
  { key: 'navLogo', label: '导航栏 logo', spec: '格式 png · 尺寸 128px × 128px', hint: '支持 jpg/png/jpeg，不超过 2MB', usage: '应用于系统收缩模式下的导航栏' },
  { key: 'favicon', label: '网页 icon', spec: '格式 ico · 尺寸 64px × 64px', hint: '支持 ico 格式，不超过 2MB', usage: '应用于网页图标', accept: '.ico' },
];

const ThemeConfig = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const meta = findMenu('/app/admin/theme');

  const [logos, setLogos] = useState(() => ({
    loginLogo: localStorage.getItem('themeLoginLogo') || '',
    navLogo: localStorage.getItem('themeNavLogo') || '',
    favicon: localStorage.getItem('themeFavicon') || '',
  }));
  const [name, setName] = useState(() => localStorage.getItem('systemName') || localStorage.getItem('platformNameZh') || '储能管家');

  const beforeUpload = (key, label) => (file) => {
    const isIco = /\.ico$/i.test(file.name);
    const isImg = ['image/png', 'image/jpeg', 'image/jpg'].includes(file.type) || /\.(png|jpe?g)$/i.test(file.name);
    const okType = key === 'favicon' ? isIco : isImg;
    if (!okType) {
      message.error(`${label}：图片格式错误，${key === 'favicon' ? '需为 ico 格式' : '支持 jpg/png/jpeg'}`);
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX) {
      message.error(`${label}：图片大小错误，不得超过 2MB`);
      return Upload.LIST_IGNORE;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setLogos((s) => ({ ...s, [key]: reader.result }));
      message.success(`${label} 已选择（本地预览，点击「保存」后生效）`);
    };
    reader.readAsDataURL(file);
    return false;
  };

  const save = () => {
    const nm = name.trim();
    if (!/^[\u4e00-\u9fa5]{4,10}$/.test(nm)) { message.error('系统名称错误：需填入 4-10 个中文字符'); return; }
    if (logos.loginLogo) localStorage.setItem('themeLoginLogo', logos.loginLogo);
    if (logos.navLogo) localStorage.setItem('themeNavLogo', logos.navLogo);
    if (logos.favicon) localStorage.setItem('themeFavicon', logos.favicon);
    localStorage.setItem('systemName', nm);
    localStorage.setItem('platformNameZh', nm);
    localStorage.setItem('platformName', nm);
    document.title = nm;
    const fav = logos.favicon || localStorage.getItem('themeFavicon');
    if (fav) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
      link.href = fav;
    }
    window.dispatchEvent(new Event('ems-theme-changed'));
    message.success('保存成功，页面已实时更新为自定义 LOGO 与系统名称');
  };

  return (
    <PageSkeleton
      title={meta ? pick(meta.label, lang) : '个性配置'}
      desc={meta ? pick(meta.desc, lang) : ''}
      crumb={meta ? [pick(meta.groupLabel, lang), pick(meta.sectionLabel, lang), pick(meta.label, lang)] : []}
      filters={[]}
      actions={[]}
    >
      <div className="panel">
        {ITEMS.map((it) => (
          <div key={it.key} style={{ display: 'flex', alignItems: 'center', gap: 18, padding: '16px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <div style={{ width: 340 }}>
              <div style={{ color: '#fff', fontWeight: 500 }}>{it.label}</div>
              <div style={{ ...labelStyle, marginTop: 4 }}>{it.spec}</div>
              <div style={{ ...labelStyle, marginTop: 2 }}>{it.usage}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <img
                src={logos[it.key] || '/logo.png'}
                alt={it.label}
                style={{ width: it.key === 'favicon' ? 48 : 64, height: it.key === 'favicon' ? 48 : 48, objectFit: 'contain', borderRadius: 8, background: 'rgba(255,255,255,0.06)', padding: 4 }}
              />
              <Upload showUploadList={false} accept={it.accept || 'image/png,image/jpeg'} beforeUpload={beforeUpload(it.key, it.label)}>
                <Button size="small" icon={<UploadOutlined />}>上传 / 替换</Button>
              </Upload>
            </div>
            <div style={{ ...labelStyle, marginLeft: 'auto' }}>{it.hint}</div>
          </div>
        ))}

        <div style={{ display: 'flex', alignItems: 'center', gap: 18, padding: '16px 0' }}>
          <div style={{ width: 340 }}>
            <div style={{ color: '#fff', fontWeight: 500 }}>系统名称</div>
            <div style={{ ...labelStyle, marginTop: 4 }}>填入 4-10 个中文字符</div>
            <div style={{ ...labelStyle, marginTop: 2 }}>应用于数据大屏标题和网页系统名称</div>
          </div>
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={10} placeholder="请输入 4-10 个中文字符" style={{ width: 260 }} />
        </div>

        <div style={{ marginTop: 16 }}>
          <Button type="primary" onClick={save}>保存</Button>
          <span style={{ ...labelStyle, marginLeft: 12 }}>未操作模块默认为储能管家原有 LOGO 和系统名称。</span>
        </div>
      </div>
    </PageSkeleton>
  );
};

export default ThemeConfig;
